import { Container, SkeletonRowLoading, TextCaption } from '@carboncopyco/ui';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import classNames from 'classnames';
import { type FC, type ReactElement, isValidElement } from 'react';
import { InfiniteList } from '../InfiniteList';

export interface Col<T> {
  alignment?: 'left' | 'center' | 'right';
  title: string | ReactElement;
  width: number;
  maxContentWidth?: number;
  render: (dataItem: T, index?: number) => ReactElement;
}

const alignmentClasses = {
  left: 'justify-start',
  center: 'justify-center',
  right: 'justify-end',
};

export interface FixedTableProps<T> {
  cols: Col<T>[];
  data: T[];
  keyName?: string;
  sideSpacing?: boolean;
  isLoading: boolean;
  isLoadingBatch?: boolean;
  handleFetchNextBatch?: () => void;
  error?: FetchBaseQueryError | SerializedError | undefined;
  infiniteListMaxHeight?: number;
  loaderPlaceholderSpace?: number;
  loader?: ReactElement;
  rowWrapper: FC<{
    children: ReactElement;
    key: string;
    item: T;
  }>;
  headerRowClassName?: string;
  noWrapperDiv?: boolean;
}

export const FixedTable = <T extends Record<string, any>>({
  cols,
  data,
  keyName = 'id',
  sideSpacing = false,
  isLoading,
  isLoadingBatch,
  handleFetchNextBatch,
  error,
  infiniteListMaxHeight,
  rowWrapper: RowWrapper,
  headerRowClassName,
  loaderPlaceholderSpace,
  noWrapperDiv,
  loader = (
    <Container>
      <SkeletonRowLoading itemsCount={3} />
    </Container>
  ),
}: FixedTableProps<T>) => {
  const content = (
    <>
      <div
        className={classNames(
          'text-left grid',
          {
            'px-6': sideSpacing,
          },
          headerRowClassName
        )}
        style={{
          gridTemplateColumns: cols.map(({ width }) => `${width}px`).join(' '),
          gridTemplateRows: 'auto',
        }}
      >
        {cols?.map(({ title }, index) => {
          return (
            <div
              key={`col${index}`}
              className="pb-3"
            >
              {isValidElement(title) ? (
                title
              ) : (
                <div
                  className={classNames(
                    'flex',
                    cols[index].alignment
                      ? [alignmentClasses[cols[index].alignment]]
                      : alignmentClasses.left
                  )}
                >
                  <TextCaption
                    label={title}
                    color="text-ds-natural-300"
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div
        className="w-full grid overflow-auto"
        style={{
          height: infiniteListMaxHeight ? `${infiniteListMaxHeight}px` : 'auto',
        }}
      >
        <InfiniteList
          isLoading={isLoading}
          loaderPlaceholderSpace={loaderPlaceholderSpace}
          loader={loader}
          isLoadingBatch={isLoadingBatch}
          onReachBottom={handleFetchNextBatch}
          error={error && { message: 'Error fetching assets' }}
        >
          {data?.map((item: T, rowIndex) => (
            <RowWrapper
              key={`row${rowIndex}${item[keyName]}`}
              item={item}
            >
              <div
                data-ds-id={`row${rowIndex}`}
                className={classNames('grid place-items-center', {
                  'px-6': sideSpacing,
                })}
                style={{
                  gridTemplateColumns: cols
                    .map(({ width }) => `${width}px`)
                    .join(' '),
                  gridTemplateRows: 'auto',
                }}
              >
                {cols.map(({ render }, colIndex) => {
                  return (
                    <div key={`cell${colIndex}`}>
                      <div
                        className={classNames(
                          'flex',
                          cols[colIndex].alignment
                            ? [alignmentClasses[cols[colIndex].alignment]]
                            : alignmentClasses.left
                        )}
                        style={{
                          width: `${cols[colIndex].width}px`,
                        }}
                      >
                        <div
                          style={{
                            width: cols[colIndex].maxContentWidth
                              ? `${cols[colIndex].maxContentWidth}px`
                              : 'initial',
                          }}
                        >
                          {render(item, rowIndex)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </RowWrapper>
          ))}
        </InfiniteList>
      </div>
    </>
  );

  return (
    <div className="w-full">
      {noWrapperDiv ? content : <div>{content}</div>}
    </div>
  );
};
